import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  InfiniteData,
} from '@tanstack/react-query';
import { MeetingAPI } from 'api/meeting';
import {
  Meeting,
  PaginatedMeetingsResponse,
  CompanyWiseSourcesResponse,
  CompanyWiseSourceMeeting,
  CompanyWiseSourceEmail,
  SignalOccurrenceWithTimePeriod,
  MeetingRecording,
  MeetingTranscript,
} from 'api/meeting/types';

export type { CompanyWiseSourceEmail } from 'api/meeting/types';

const ALL_MEETINGS_KEY = ['meetings'] as const;
const ALL_MEETINGS_WITH_FILTERS_KEY = ['meetings', 'filtered'] as const;
const SELECTED_MEETING_KEY = ['selectedMeeting'] as const;

export const useGetAllMeetingsQuery = (
  queryOptions?: UseQueryOptions<
    Meeting[],
    Error,
    Meeting[],
    typeof ALL_MEETINGS_KEY
  >,
): UseQueryResult<Meeting[], Error> => {
  return useQuery({
    queryKey: ALL_MEETINGS_KEY,
    queryFn: () => MeetingAPI.getAllMeetings(),
    ...queryOptions,
  });
};

// Simple query - returns first page only
export const useGetAllMeetingsQueryBasedOnFilter = (
  filters: GroupedFilter,
  params: {
    enabled: boolean;
    limit?: number;
  },
  queryOptions?: UseQueryOptions<
    PaginatedMeetingsResponse,
    Error,
    Meeting[],
    readonly [string, string, GroupedFilter]
  >,
): UseQueryResult<Meeting[], Error> => {
  return useQuery({
    queryKey: [...ALL_MEETINGS_WITH_FILTERS_KEY, filters] as const,
    queryFn: () =>
      MeetingAPI.getAllMeetingsWithFilters(filters, {
        offset: 0,
        limit: params.limit || 25,
      }),
    enabled: params.enabled,
    select: (response: PaginatedMeetingsResponse) => response.data,
    ...queryOptions,
  });
};

// Infinite query with offset-based pagination
export const useGetAllMeetingsQueryBasedOnFilterPaginated = (
  filters: GroupedFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    searchTerm?: string;
  },
  queryOptions?: UseInfiniteQueryOptions<
    PaginatedMeetingsResponse,
    Error,
    InfiniteData<PaginatedMeetingsResponse>,
    PaginatedMeetingsResponse,
    readonly [
      string,
      string,
      GroupedFilter | undefined,
      string | undefined,
      'paginated',
    ],
    number
  >,
): UseInfiniteQueryResult<InfiniteData<PaginatedMeetingsResponse>, Error> => {
  return useInfiniteQuery({
    queryKey: [
      ...ALL_MEETINGS_WITH_FILTERS_KEY,
      filters,
      params.searchTerm,
      'paginated',
    ] as const,
    queryFn: ({ pageParam = 0 }) =>
      MeetingAPI.getAllMeetingsWithFilters(filters, {
        offset: pageParam,
        limit: params.limit || 25,
        searchTerm: params.searchTerm,
      }),
    enabled: params.enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = (allPages.length - 1) * (params.limit || 25);
      const hasMore = lastPage.data.length === (params.limit || 25);

      return hasMore ? currentOffset + (params.limit || 25) : undefined;
    },
    ...queryOptions,
  });
};

// Helper hook that flattens infinite query data for easier use
export const useGetAllMeetingsQueryWithScrollPagination = (
  filters: GroupedFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    searchTerm?: string;
  },
) => {
  const infiniteQuery = useGetAllMeetingsQueryBasedOnFilterPaginated(
    filters,
    params,
  );

  const meetings =
    infiniteQuery.data?.pages?.flatMap((page) => page.data) ?? [];
  const totalCount = infiniteQuery.data?.pages?.[0]?.count ?? 0;

  return {
    meetings,
    totalCount,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
  };
};

const DEFAULT_SELECTED_MEETING = {
  meetingId: 0,
  meetingName: '',
};

export const useSelectedMeetingId = () => {
  const queryClient = useQueryClient();

  // Subscribe to the cache so setQueryData triggers re-renders
  const { data: selectedMeeting } = useQuery({
    queryKey: SELECTED_MEETING_KEY,
    queryFn: () =>
      (queryClient.getQueryData(SELECTED_MEETING_KEY) as
        | { meetingId: number; meetingName: string }
        | undefined) ?? DEFAULT_SELECTED_MEETING,
    initialData: DEFAULT_SELECTED_MEETING,
    staleTime: Infinity,
  });

  const value = selectedMeeting ?? DEFAULT_SELECTED_MEETING;

  return {
    selectedMeetingId: value.meetingId === 0 ? undefined : value.meetingId,
    selectedMeetingName: value.meetingName || undefined,
    setSelectedMeeting: (meetingId: number, meetingName: string) => {
      queryClient.setQueryData(SELECTED_MEETING_KEY, {
        meetingId,
        meetingName,
      });
    },

    clearSelectedMeeting: () => {
      queryClient.setQueryData(SELECTED_MEETING_KEY, DEFAULT_SELECTED_MEETING);
    },
  };
};

export const useSearchMeetingsByCompanyIdQuery = (
  companyId: number,
  options?: {
    offset?: number;
    limit?: number;
    searchTerm?: string;
  },
  queryOptions?: Omit<
    UseQueryOptions<
      PaginatedMeetingsResponse,
      Error,
      PaginatedMeetingsResponse,
      (string | number | undefined)[]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<PaginatedMeetingsResponse, Error> => {
  return useQuery({
    queryKey: [
      'meetings',
      'search',
      companyId,
      options?.offset,
      options?.limit,
      options?.searchTerm,
    ],
    queryFn: () => MeetingAPI.searchMeetingsByCompanyId(companyId, options),
    enabled: !!companyId,
    ...queryOptions,
  });
};

export const useCompanyWiseSourcesQuery = <
  T extends
    | CompanyWiseSourceMeeting
    | CompanyWiseSourceEmail = CompanyWiseSourceMeeting,
>(
  params: {
    sourceType: 'MEETING' | 'EMAIL';
    limit: number;
    offset: number;
    companyId: number;
    templateIds?: number[];
    isEnabled?: boolean;
    companyPageDocumentId?: number;
    searchTerm?: string;
  },
  queryOptions?: Omit<
    UseQueryOptions<
      CompanyWiseSourcesResponse<T>,
      Error,
      CompanyWiseSourcesResponse<T>,
      (string | number | boolean | number[] | undefined)[]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<CompanyWiseSourcesResponse<T>, Error> => {
  return useQuery({
    queryKey: [
      'company-wise-sources',
      params.sourceType,
      params.companyId,
      params.offset,
      params.limit,
      params.templateIds,
      params.isEnabled,
      params.companyPageDocumentId,
      params.searchTerm,
    ],
    queryFn: () => MeetingAPI.getCompanyWiseSources<T>(params),
    enabled: !!params.companyId && params.companyId > 0,
    ...queryOptions,
  });
};

export const useMeetingRecordingQuery = (
  meetingId?: number,
  queryOptions?: UseQueryOptions<
    MeetingRecording,
    Error,
    MeetingRecording,
    (string | number | undefined)[]
  >,
): UseQueryResult<MeetingRecording, Error> => {
  return useQuery({
    queryKey: ['meeting-recording', meetingId],
    queryFn: () => MeetingAPI.getMeetingRecording(meetingId ?? -1),
    enabled: meetingId != null,
    ...queryOptions,
  });
};
// Regular query for company v2 sources
export const useCompanyV2SourcesQuery = <
  T extends
    | CompanyWiseSourceMeeting
    | CompanyWiseSourceEmail = CompanyWiseSourceMeeting,
>(
  params: {
    documentId: number;
    companyId: number;
    sourceType: 'MEETING' | 'EMAIL';
    limit: number;
    offset: number;
    fetchUsed?: boolean;
    isEnabled?: boolean;
  },
  queryOptions?: Omit<
    UseQueryOptions<
      CompanyWiseSourcesResponse<T>,
      Error,
      CompanyWiseSourcesResponse<T>,
      (string | number | boolean | undefined)[]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<CompanyWiseSourcesResponse<T>, Error> => {
  return useQuery({
    queryKey: [
      'company-v2-sources',
      params.documentId,
      params.companyId,
      params.sourceType,
      params.offset,
      params.limit,
      params.fetchUsed,
      params.isEnabled,
    ],
    queryFn: () => MeetingAPI.getCompanyV2Sources<T>(params),
    enabled: !!params.documentId && !!params.companyId,
    ...queryOptions,
  });
};

export const useMeetingTranscriptQuery = (
  meetingId?: number,
  queryOptions?: UseQueryOptions<
    MeetingTranscript,
    Error,
    MeetingTranscript,
    (string | number | undefined)[]
  >,
): UseQueryResult<MeetingTranscript, Error> => {
  return useQuery({
    queryKey: ['meeting-transcript', meetingId],
    queryFn: () => MeetingAPI.getMeetingTranscript(meetingId ?? -1),
    enabled: meetingId != null,
    ...queryOptions,
  });
};
export const useSignalOccurrencesForMeetingWithTimePeriodQuery = (
  meetingId?: number,
  queryOptions?: UseQueryOptions<
    SignalOccurrenceWithTimePeriod[],
    Error,
    SignalOccurrenceWithTimePeriod[],
    (string | number | undefined)[]
  >,
): UseQueryResult<SignalOccurrenceWithTimePeriod[], Error> => {
  return useQuery({
    queryKey: ['signal-occurrences-for-meeting-with-time-period', meetingId],
    queryFn: () =>
      MeetingAPI.getSignalOccurrencesForMeetingWithTimePeriod(meetingId ?? -1),
    enabled: meetingId != null,
    ...queryOptions,
  });
};
// Infinite query for company v2 sources with scroll pagination
export const useCompanyV2SourcesInfiniteQuery = <
  T extends
    | CompanyWiseSourceMeeting
    | CompanyWiseSourceEmail = CompanyWiseSourceMeeting,
>(
  params: {
    documentId: number;
    companyId: number;
    sourceType: 'MEETING' | 'EMAIL';
    limit?: number;
    fetchUsed?: boolean;
    isEnabled?: boolean;
  },
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      CompanyWiseSourcesResponse<T>,
      Error,
      InfiniteData<CompanyWiseSourcesResponse<T>>,
      CompanyWiseSourcesResponse<T>,
      (string | number | boolean | undefined)[],
      number
    >,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >,
): UseInfiniteQueryResult<
  InfiniteData<CompanyWiseSourcesResponse<T>>,
  Error
> => {
  const limit = params.limit || 10;

  return useInfiniteQuery({
    queryKey: [
      'company-v2-sources',
      params.documentId,
      params.companyId,
      params.sourceType,
      params.fetchUsed,
      params.isEnabled,
    ],
    queryFn: ({ pageParam = 0 }) =>
      MeetingAPI.getCompanyV2Sources<T>({
        ...params,
        limit,
        offset: pageParam,
      }),
    enabled: !!params.documentId && !!params.companyId,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) {
        return undefined;
      }

      return allPages.length * limit;
    },
    ...queryOptions,
  });
};

// Helper hook that flattens infinite query data for easier use
export const useCompanyV2SourcesWithScrollPagination = <
  T extends
    | CompanyWiseSourceMeeting
    | CompanyWiseSourceEmail = CompanyWiseSourceMeeting,
>(params: {
  documentId: number;
  companyId: number;
  sourceType: 'MEETING' | 'EMAIL';
  limit?: number;
  fetchUsed?: boolean;
  isEnabled?: boolean;
}) => {
  const infiniteQuery = useCompanyV2SourcesInfiniteQuery<T>(params);

  const sources = infiniteQuery.data?.pages?.flatMap((page) => page.data) ?? [];

  return {
    sources,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
  };
};
